import { ASTROLABE_IDS, type AstrolabeId, type TeamId } from "@scb/protocol";
import type { SimBall, TableSpec } from "@scb/sim";
import { slotCounts } from "@scb/sim";

/**
 * Astrolabe 슬롯 점유 현황.
 * `occupancy[astrolabeId][slotIndex]`가 그 슬롯을 차지한 팀, 비었으면 null.
 */
export type Occupancy = Record<AstrolabeId, (TeamId | null)[]>;

export function emptyOccupancy(table: TableSpec): Occupancy {
  const counts = slotCounts(table);
  return {
    1: new Array<TeamId | null>(counts[1]).fill(null),
    2: new Array<TeamId | null>(counts[2]).fill(null),
    3: new Array<TeamId | null>(counts[3]).fill(null),
  };
}

export function buildOccupancy(table: TableSpec, balls: readonly SimBall[]): Occupancy {
  const occ = emptyOccupancy(table);
  for (const b of balls) {
    if (b.state !== "captured") continue;
    if (b.astrolabe === null || b.slot === null) continue;
    const row = occ[b.astrolabe];
    if (b.slot < 0 || b.slot >= row.length) continue;
    row[b.slot] = b.team;
  }
  return occ;
}

export function cloneOccupancy(occ: Occupancy): Occupancy {
  return { 1: [...occ[1]], 2: [...occ[2]], 3: [...occ[3]] };
}

/**
 * `slot`을 포함해 같은 팀 공이 연속으로 이어진 길이 (Chaining / Stringing).
 * 자기 자신만 있으면 1을 반환한다.
 */
export function chainLengthAt(occ: Occupancy, astrolabe: AstrolabeId, slot: number): number {
  const row = occ[astrolabe];
  const team = row[slot];
  if (team === null || team === undefined) return 0;

  let length = 1;
  for (let i = slot - 1; i >= 0 && row[i] === team; i--) length++;
  for (let i = slot + 1; i < row.length && row[i] === team; i++) length++;
  return length;
}

/** 해당 Astrolabe가 한 팀의 공으로 가득 찼는지 (Grand Slam / 만관). */
export function grandSlamTeam(occ: Occupancy, astrolabe: AstrolabeId): TeamId | null {
  const row = occ[astrolabe];
  if (row.length === 0) return null;
  const first = row[0];
  if (first === null) return null;
  for (const cell of row) {
    if (cell !== first) return null;
  }
  return first;
}

/**
 * 한 팀이 통째로 채운 판을 찾는다 (만관 즉시 종료 판정).
 *
 * 가장 가까운 판(1번)부터 본다 — 한 샷으로 두 판이 동시에 완성되는 건 불가능하므로
 * 실질적으로 순서는 의미가 없지만, 결정론을 위해 순회 순서를 고정해 둔다.
 */
export function grandSlamPlate(
  occ: Occupancy,
): { astrolabe: AstrolabeId; team: TeamId } | null {
  for (const id of ASTROLABE_IDS) {
    const team = grandSlamTeam(occ, id);
    if (team !== null) return { astrolabe: id, team };
  }
  return null;
}

export function isFull(occ: Occupancy, astrolabe: AstrolabeId): boolean {
  return occ[astrolabe].every((cell) => cell !== null);
}

/** 모든 Astrolabe가 가득 찼는지 — 라운드를 더 진행할 수 없는 상태. */
export function allPlatesFull(occ: Occupancy): boolean {
  return ASTROLABE_IDS.every((id) => isFull(occ, id));
}
